import type { GameEngine } from '@shared/gameContracts';
import type {
  GameState, SetupConfig, FullSettlement, BetStatusData, HoleScore,
  RebuildRoundStateInput, RebuildRoundStateResult,
} from '@shared/gameTypes';
import {
  generateId, calculateLowManHandicaps, playerReceivesStrokeOnHole,
  isRoundComplete as sharedIsRoundComplete,
  finalizeRound as sharedFinalizeRound,
  unfinalizeRound as sharedUnfinalizeRound,
  getStrokesOnHole, calculateNetScore,
  getPlayingOrder,
} from '@/lib/gameUtils';

function settleGame(_state: GameState): FullSettlement {
  return {
    perLeg: {
      Front: { unitsForA: 0, amountForA: 0 },
      Back: { unitsForA: 0, amountForA: 0 },
      Overall: { unitsForA: 0, amountForA: 0 },
    },
    totalAmountForA: 0,
    totalAmountForB: 0,
  };
}

function calculateBetStatus(state: GameState): BetStatusData {
  const teamALabel = state.teams[0].players.length === 1
    ? (state.teams[0].players[0]?.name ?? 'Player')
    : `${state.teams[0].players[0]?.name || ''}/${state.teams[0].players[1]?.name || ''}`;
  const teamBLabel = state.teams[1].players.length === 1
    ? (state.teams[1].players[0]?.name ?? 'Player')
    : `${state.teams[1].players[0]?.name || ''}/${state.teams[1].players[1]?.name || ''}`;

  return {
    lastCompletedHole: null,
    holesCompleted: 0,
    closedBets: [],
    openBetsByLeg: [],
    openNetSummary: { netUnitsForA: 0, netDollarsForA: 0, totalActiveBets: 0 },
    projectedTotalA: { units: 0, dollars: 0 },
    projectedTotalB: { units: 0, dollars: 0 },
    teamALabel,
    teamBLabel,
  };
}

function initializeGameState(config: SetupConfig): GameState {
  const playersWithStrokes = calculateLowManHandicaps(config.players, config.courseData);

  const team1Players = playersWithStrokes.filter((_, i) => config.players[i].teamIndex === 0);
  const team2Players = playersWithStrokes.filter((_, i) => config.players[i].teamIndex === 1);

  const teams: GameState['teams'] = [
    { id: generateId(), players: team1Players },
    { id: generateId(), players: team2Players },
  ];

  const scores: HoleScore[] = [];
  const allPlayers = [...team1Players, ...team2Players];

  for (const hole of config.course.holes) {
    for (const player of allPlayers) {
      scores.push({
        playerId: player.id,
        holeNumber: hole.number,
        grossScore: null,
        netScore: null,
        receivesStroke: playerReceivesStrokeOnHole(player, hole),
      });
    }
  }

  const startingHole = config.startingHole ?? 1;

  return {
    gameType: 'skins',
    matchFormat: config.matchFormat,
    gameMode: config.gameMode,
    teams,
    holes: config.course.holes,
    scores,
    presses: [],
    nassauStatus: { front: 0, back: 0, overall: 0 },
    currentHole: startingHole,
    topSideOverallPressUsed: false,
    bottomSideOverallPressUsed: false,
    stakes: config.stakes,
    startingHole,
    isFinalized: false,
    courseDataId: config.courseData.id,
  };
}

function updateScore(
  state: GameState,
  playerId: string,
  holeNumber: number,
  grossScore: number | null
): GameState {
  const hole = state.holes.find(h => h.number === holeNumber);
  if (!hole) return state;

  const player = [...state.teams[0].players, ...state.teams[1].players].find(p => p.id === playerId);
  if (!player) return state;

  const receivesStroke = playerReceivesStrokeOnHole(player, hole);
  const strokeCount = getStrokesOnHole(player, hole);
  const netScore = grossScore !== null ? calculateNetScore(grossScore, receivesStroke, strokeCount) : null;

  const updatedScores = state.scores.map(s => {
    if (s.playerId === playerId && s.holeNumber === holeNumber) {
      return { ...s, grossScore, netScore, receivesStroke };
    }
    return s;
  });

  return {
    ...state,
    scores: updatedScores,
  };
}

function updateGameStateSettings(
  state: GameState,
  _config: SetupConfig
): GameState {
  return state;
}

function getMatchStatus(_state: GameState) {
  return { front: 0, back: 0, overall: 0 };
}

function rebuildRoundState(input: RebuildRoundStateInput): RebuildRoundStateResult {
  const { roundConfig, scores: scoreInputs } = input;
  const allPlayers = roundConfig.teams.flatMap(t => t.players);
  const playingOrder = getPlayingOrder(roundConfig.startingHole);

  const fullScores: HoleScore[] = [];
  for (const hole of roundConfig.holes) {
    for (const player of allPlayers) {
      fullScores.push({
        playerId: player.id,
        holeNumber: hole.number,
        grossScore: null,
        netScore: null,
        receivesStroke: playerReceivesStrokeOnHole(player, hole),
      });
    }
  }

  let state: GameState = {
    gameType: 'skins',
    matchFormat: roundConfig.matchFormat,
    gameMode: roundConfig.gameMode,
    teams: roundConfig.teams,
    holes: roundConfig.holes,
    scores: fullScores,
    presses: [],
    nassauStatus: { front: 0, back: 0, overall: 0 },
    currentHole: roundConfig.startingHole,
    topSideOverallPressUsed: false,
    bottomSideOverallPressUsed: false,
    stakes: roundConfig.stakes,
    startingHole: roundConfig.startingHole,
    isFinalized: roundConfig.isFinalized ?? false,
    courseDataId: roundConfig.courseDataId,
    roundId: roundConfig.roundId,
  };

  let activeHole: number | null = null;

  for (const holeNumber of playingOrder) {
    const holeScores = scoreInputs.filter(s => s.holeNumber === holeNumber);

    for (const si of holeScores) {
      if (si.grossScore === null) continue;
      const hole = roundConfig.holes.find(h => h.number === holeNumber);
      if (!hole) continue;
      const player = allPlayers.find(p => p.id === si.playerId);
      if (!player) continue;
      const receivesStroke = playerReceivesStrokeOnHole(player, hole);
      const strokeCount = getStrokesOnHole(player, hole);
      const netScore = calculateNetScore(si.grossScore, receivesStroke, strokeCount);
      state = {
        ...state,
        scores: state.scores.map(s =>
          s.playerId === si.playerId && s.holeNumber === holeNumber
            ? { ...s, grossScore: si.grossScore, netScore, receivesStroke }
            : s
        ),
      };
    }

    const holeComplete = allPlayers.every(p => {
      const s = state.scores.find(sc => sc.playerId === p.id && sc.holeNumber === holeNumber);
      return s && s.grossScore !== null;
    });

    if (!holeComplete) {
      if (activeHole === null) activeHole = holeNumber;
      continue;
    }
    state.currentHole = holeNumber;
  }

  if (activeHole === null && !sharedIsRoundComplete(state)) {
    activeHole = playingOrder[0];
  }

  return { gameState: state, activeHole };
}

export const skinsEngine: GameEngine = {
  gameType: 'skins',
  settleGame,
  calculateBetStatus,
  getMatchStatus,
  initializeGameState,
  updateScore,
  updateGameStateSettings,
  isRoundComplete: sharedIsRoundComplete,
  finalizeRound: sharedFinalizeRound,
  unfinalizeRound: sharedUnfinalizeRound,
  rebuildRoundState,
};
